import React from 'react';
import { HeroSection } from '../../components/HeroSection';
import { motion } from 'framer-motion';
import { Scale, Building2, Users2, Stethoscope, Briefcase, GraduationCap } from 'lucide-react';
export function SystemsPower() {
  const systems = [{
    icon: <Stethoscope className="h-8 w-8" data-id="element-1475" />,
    title: 'Medical System',
    description: 'Healthcare providers pathologize fatness, deny care, and prescribe weight loss as cure-all.',
    examples: ['BMI as diagnostic tool', 'Weight-based dosing discrimination', 'Surgical denial based on size']
  }, {
    icon: <Briefcase className="h-8 w-8" data-id="element-1476" />,
    title: 'Employment',
    description: 'Hiring discrimination, wage gaps, and workplace harassment based on body size.',
    examples: ['Size-based hiring bias', 'Appearance clauses', 'Lack of accommodations']
  }, {
    icon: <Building2 className="h-8 w-8" data-id="element-1477" />,
    title: 'Built Environment',
    description: 'Physical spaces designed to exclude and punish fat bodies through hostile architecture.',
    examples: ['Narrow seating', 'Weight limits on furniture', 'Inaccessible public transit']
  }, {
    icon: <Scale className="h-8 w-8" data-id="element-1478" />,
    title: 'Legal System',
    description: 'No federal protections against size discrimination. Fat people can be legally fired, denied housing.',
    examples: ['No ADA coverage', 'Child custody bias', 'Insurance discrimination']
  }, {
    icon: <GraduationCap className="h-8 w-8" data-id="element-1479" />,
    title: 'Education',
    description: 'Schools enforce diet culture, enable bullying, and create hostile learning environments.',
    examples: ['BMI report cards', 'PE humiliation', 'Desk/chair exclusion']
  }, {
    icon: <Users2 className="h-8 w-8" data-id="element-1480" />,
    title: 'Social & Cultural',
    description: 'Media representation, dating discrimination, and everyday microaggressions normalize anti-fatness.',
    examples: ['Fat villain trope', 'Dating app filters', 'Public commentary']
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-1481">
      <HeroSection title="Systems, Power & Anti-Fat Bias" subtitle="Understanding how anti-fatness operates at structural, institutional, and interpersonal levels." tag="Fat Liberation & Justice" bgColor="magenta" data-id="element-1482" />

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1483">
        <div className="prose prose-lg" data-id="element-1484">
          <p className="text-xl text-gray-700 leading-relaxed" data-id="element-1485">
            Anti-fat bias isn't just individual prejudice—it's a{' '}
            <strong className="text-brand-magenta" data-id="element-1486">system of oppression</strong>{' '}
            embedded in our institutions, policies, and cultural norms.
            Understanding these systems is the first step toward dismantling
            them.
          </p>
        </div>
      </section>

      {/* Systems Grid */}
      <section className="py-16 bg-white" data-id="element-1487">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1488">
          <h2 className="text-3xl font-black uppercase mb-12 text-brand-black" data-id="element-1489">
            Six Interconnected Systems
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1490">
            {systems.map((system, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} viewport={{
            once: true
          }} className="border-2 border-brand-black bg-brand-cream p-6 hover:shadow-xl transition-shadow" data-id="element-1491">
                <div className="text-brand-magenta mb-4" data-id="element-1492">{system.icon}</div>
                <h3 className="text-xl font-black uppercase mb-3 text-brand-black" data-id="element-1493">
                  {system.title}
                </h3>
                <p className="text-gray-700 mb-4 text-sm leading-relaxed" data-id="element-1494">
                  {system.description}
                </p>
                <div className="space-y-2" data-id="element-1495">
                  <p className="text-xs font-bold uppercase text-gray-500 mb-2" data-id="element-1496">
                    Examples:
                  </p>
                  {system.examples.map((example, i) => <div key={i} className="flex items-start" data-id="element-1497">
                      <span className="text-brand-magenta mr-2" data-id="element-1498">•</span>
                      <span className="text-sm text-gray-600" data-id="element-1499">{example}</span>
                    </div>)}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Power Analysis */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-1500">
        <h2 className="text-3xl font-black uppercase mb-12 text-brand-black border-b-4 border-brand-magenta inline-block pb-2" data-id="element-1501">
          Who Benefits?
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-1502">
          <div className="bg-brand-magenta text-white p-8 rounded-sm" data-id="element-1503">
            <h3 className="text-2xl font-black uppercase mb-6" data-id="element-1504">
              The Diet Industry
            </h3>
            <p className="text-lg mb-4 opacity-90" data-id="element-1505">
              $72 billion annually in the US alone
            </p>
            <ul className="space-y-3 text-sm" data-id="element-1506">
              <li className="flex items-start" data-id="element-1507">
                <span className="mr-2" data-id="element-1508">→</span>
                <span data-id="element-1509">Weight loss programs, apps, and supplements</span>
              </li>
              <li className="flex items-start" data-id="element-1510">
                <span className="mr-2" data-id="element-1511">→</span>
                <span data-id="element-1512">Pharmaceutical companies pushing weight loss drugs</span>
              </li>
              <li className="flex items-start" data-id="element-1513">
                <span className="mr-2" data-id="element-1514">→</span>
                <span data-id="element-1515">Bariatric surgery industry</span>
              </li>
              <li className="flex items-start" data-id="element-1516">
                <span className="mr-2" data-id="element-1517">→</span>
                <span data-id="element-1518">"Wellness" rebranding of diet culture</span>
              </li>
            </ul>
          </div>

          <div className="bg-brand-navy text-white p-8 rounded-sm" data-id="element-1519">
            <h3 className="text-2xl font-black uppercase mb-6" data-id="element-1520">
              White Supremacy
            </h3>
            <p className="text-lg mb-4 opacity-90" data-id="element-1521">
              Anti-fatness is rooted in racism
            </p>
            <ul className="space-y-3 text-sm" data-id="element-1522">
              <li className="flex items-start" data-id="element-1523">
                <span className="mr-2" data-id="element-1524">→</span>
                <span data-id="element-1525">Body ideals based on European beauty standards</span>
              </li>
              <li className="flex items-start" data-id="element-1526">
                <span className="mr-2" data-id="element-1527">→</span>
                <span data-id="element-1528">Pathologization of Black and Indigenous bodies</span>
              </li>
              <li className="flex items-start" data-id="element-1529">
                <span className="mr-2" data-id="element-1530">→</span>
                <span data-id="element-1531">Eugenics history of BMI and weight science</span>
              </li>
              <li className="flex items-start" data-id="element-1532">
                <span className="mr-2" data-id="element-1533">→</span>
                <span data-id="element-1534">
                  Controlling Black women's bodies through "health" discourse
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Intersections */}
      <section className="py-20 bg-gray-50" data-id="element-1535">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1536">
          <h2 className="text-3xl font-black uppercase mb-8 text-brand-black" data-id="element-1537">
            Intersectional Impact
          </h2>
          <p className="text-lg text-gray-700 mb-8" data-id="element-1538">
            Anti-fat bias doesn't exist in isolation. It compounds with other
            systems of oppression:
          </p>

          <div className="space-y-6" data-id="element-1539">
            <div className="bg-white border-l-4 border-brand-magenta p-6" data-id="element-1540">
              <h4 className="font-bold text-lg mb-2" data-id="element-1541">Race + Fatness</h4>
              <p className="text-gray-700" data-id="element-1542">
                Fat Black people face both anti-Blackness and anti-fatness.
                Medical racism means less pain management, more coercion into
                weight loss, higher maternal mortality.
              </p>
            </div>

            <div className="bg-white border-l-4 border-brand-magenta p-6" data-id="element-1543">
              <h4 className="font-bold text-lg mb-2" data-id="element-1544">Disability + Fatness</h4>
              <p className="text-gray-700" data-id="element-1545">
                Fat disabled people are blamed for their disabilities. Mobility
                aids are denied. Fatness is treated as the "real" problem to
                solve.
              </p>
            </div>

            <div className="bg-white border-l-4 border-brand-magenta p-6" data-id="element-1546">
              <h4 className="font-bold text-lg mb-2" data-id="element-1547">
                Trans Identity + Fatness
              </h4>
              <p className="text-gray-700" data-id="element-1548">
                Trans fat people face additional barriers to gender-affirming
                care. Body size is weaponized to deny hormones and surgery.
              </p>
            </div>

            <div className="bg-white border-l-4 border-brand-magenta p-6" data-id="element-1549">
              <h4 className="font-bold text-lg mb-2" data-id="element-1550">Class + Fatness</h4>
              <p className="text-gray-700" data-id="element-1551">
                Poor fat people are blamed for their poverty. "Healthy" food is
                expensive. Weight loss is marketed as path to economic mobility.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}